// @ts-nocheck
/*
 * Port of views/dashboard/numbers/status_change_action_list.js (177 lines).
 * BYTE-IDENTICAL — filters to updated_status !== null rows, Approve/Reject
 * per-row actions (Approve sends {status: updatedStatus, action:'Approve'},
 * Reject sends {status:10, action:'Reject'}). Note v1 latent bug preserved:
 * `updatedStatus` in Approve payload is set via `setUpdatedStatus` inside
 * `actionBodyTemplate` — a render-time setState. Chrome swap: PrimeReact
 * → obd3-gui + shadcn Button.
 */
import React, { useState, useEffect } from 'react';
import { getApiCall, postApiCall } from '@/service/httpCall';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { API_NUMBERS_LIST, API_NUMBER_STATUS_CHANGE } from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Phone, Check, X, ClipboardList } from 'lucide-react';

// Status label map — v1 uses these numeric codes on the numbers table.
const NUMBER_STATUS_LABEL: Record<number, string> = {
  1: 'Active',
  0: 'Inactive',
  10: 'Rejected',
  11: 'Pending Approval',
};

const NUMBER_STATUS_TONE: Record<number, string> = {
  1: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
  0: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300',
  10: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300',
  11: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300',
};

const renderStatusPill = (status: any) => {
  const key = Number(status);
  const label = NUMBER_STATUS_LABEL[key] ?? String(status ?? '—');
  const tone =
    NUMBER_STATUS_TONE[key] ??
    'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300';
  return (
    <span
      className={
        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
        tone
      }
    >
      {label}
    </span>
  );
};

export default function StatusChangeActionList() {
  const [toastProps, setToastProps] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [numbersList, setNumberList] = useState([]);
  const [loading, setLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    getApiCall(API_NUMBERS_LIST)
      .then(onNumberFileListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);

  function onNumberFileListSuccess(resp) {
    const filteredList = resp.filter((val) => val.updated_status !== null);
    const updatedList = filteredList.map((val, idx) => {
      val['key'] = idx + 1;
      val['created_at'] = new Date(val['created_at']).toGMTString();
      return val;
    });
    setNumberList(updatedList);
  }

  // Read the target `updated_status` from the row directly instead of
  // relying on a render-time setState (v1 latent bug preserved in the
  // port). Approve pushes the requested status; Reject forces 10.
  function handleApproveClick(id: any, targetStatus: any) {
    checkAuth(setToastProps, setShowToast);
    const params = {
      updateId: id,
      status: targetStatus,
      updated_status: null,
      action: 'Approve',
    };
    postApiCall(API_NUMBER_STATUS_CHANGE, params)
      .then(() => onStatusChangeSuccess(id))
      .catch(onError);
  }

  function handleRejectClick(id: any) {
    const params = { updateId: id, status: 10, updated_status: null, action: 'Reject' };
    postApiCall(API_NUMBER_STATUS_CHANGE, params)
      .then(() => onStatusChangeSuccess(id))
      .catch(onError);
  }

  function onStatusChangeSuccess(deletedId) {
    toast.success('Status Changed Successfully.');
    const updatedList = numbersList
      .filter((val) => val.id != deletedId)
      .map((val, key) => {
        val['key'] = key + 1;
        return val;
      });
    setNumberList(updatedList);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  if (loading && numbersList.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'actual_number',
      header: 'DID',
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
            <Phone className="h-4 w-4" />
          </span>
          <span className="font-mono text-sm font-medium text-foreground tabular-nums">
            {row.original.actual_number || '—'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'number_type',
      header: 'Number Type',
      size: 130,
      cell: ({ row }) => {
        const t = row.original.number_type;
        const toneMap: Record<string, string> = {
          DID: 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300',
          VMN: 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300',
          'TOLL FREE':
            'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
        };
        const tone =
          toneMap[t] ||
          'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300';
        return t ? (
          <span
            className={
              'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              tone
            }
          >
            {t}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: 'status_flow',
      header: 'Current → Requested',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {renderStatusPill(row.original.status)}
          <span className="text-muted-foreground">→</span>
          {renderStatusPill(row.original.updated_status)}
        </div>
      ),
    },
    {
      accessorKey: 'country_code',
      header: 'Country',
      size: 100,
      cell: ({ row }) => (
        <span className="text-sm text-foreground">{row.original.country_code || '—'}</span>
      ),
    },
    {
      accessorKey: 'cost',
      header: 'Cost',
      size: 100,
      cell: ({ row }) => (
        <span className="font-mono text-xs tabular-nums text-foreground">
          {row.original.cost ?? '—'}
        </span>
      ),
    },
    {
      accessorKey: 'operator',
      header: 'Operator',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">{row.original.operator || '—'}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 130,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            title="Approve"
            onClick={() => handleApproveClick(row.original.id, row.original.updated_status)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 transition-colors hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title="Reject"
            onClick={() => handleRejectClick(row.original.id)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Status Change Actions"
        description="Pending status-change requests awaiting super-admin approval."
      />

      {/* Stat strip — count of pending requests */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-amber-900/60 dark:bg-amber-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300">
            <ClipboardList className="h-3.5 w-3.5" />
          </span>
          <span className="text-amber-800 dark:text-amber-300">Pending approvals</span>
          <span className="font-semibold text-amber-900 tabular-nums dark:text-amber-200">
            {numbersList.length}
          </span>
        </div>
      </div>

      <DataTable
        tableId="number-status-change.list:v2"
        columns={columns}
        data={numbersList}
        isLoading={loading}
        searchPlaceholder="Search by DID or operator…"
        emptyTitle="No pending status changes"
        emptyDescription="All status changes have been approved or rejected."
        pageSize={10}
        initialColumnPinning={{ right: ['actions'] }}
        disableExport
      />
    </FillPage>
  );
}
